"use client";

import React from "react";

export interface CommonTag {
  readonly label: string;
  readonly value: string;
}

interface CommonTagChipsProps {
  /** Current tags array */
  currentTags: string[];
  /** Callback when tags change */
  onTagsChange: (tags: string[]) => void;
  /** Array of common tags to display as chips */
  commonTags: readonly CommonTag[];
  /** Number of columns for the grid layout */
  columns?: 2 | 3 | 4;
  /** Additional CSS classes */
  className?: string;
}

export const CommonTagChips: React.FC<CommonTagChipsProps> = ({
  currentTags = [],
  onTagsChange,
  commonTags,
  columns = 2,
  className = "",
}) => {
  const gridCols = {
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-2 md:grid-cols-4",
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-1 mb-1.5 ${className}`}>
      {commonTags.map((tag) => {
        const isSelected = currentTags.includes(tag.value);

        return (
          <button
            key={tag.value}
            type="button"
            onClick={() => {
              let newTags;

              if (isSelected) {
                // Remove tag if already selected
                newTags = currentTags.filter((t) => t !== tag.value);
              } else {
                // Add tag if not selected
                newTags = [...currentTags, tag.value];
              }

              onTagsChange(newTags);
            }}
            className={`
              px-1 py-0.5 text-[10px] font-medium rounded-full border transition-colors
              ${
                isSelected
                  ? "bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/40 dark:border-blue-800 dark:text-blue-300"
                  : "bg-card-50 border-gray-200 text-gray-600 hover:bg-card-100 dark:border-gray-700 dark:text-gray-400"
              }
            `}
          >
            {tag.label}
          </button>
        );
      })}
    </div>
  );
};

// Predefined tag sets for different contexts
export const TAG_SETS = {
  TASK: [
    { label: "operations", value: "operations" },
    { label: "follow-up", value: "follow-up" },
    { label: "quote", value: "quote" },
    { label: "opportunity", value: "opportunity" },
    { label: "meca_sm", value: "meca_sm" },
    { label: "wtc", value: "wtc" },
  ],
  CONTACT: [
    { label: "vip", value: "vip" },
    { label: "enterprise", value: "enterprise" },
    { label: "startup", value: "startup" },
    { label: "prospect", value: "prospect" },
  ],
  TICKET: [
    { label: "urgent", value: "urgent" },
    { label: "network", value: "network" },
    { label: "hardware", value: "hardware" },
    { label: "software", value: "software" },
  ],
  TIME_ENTRY: [
    { label: "development", value: "development" },
    { label: "support", value: "support" },
    { label: "consultation", value: "consultation" },
    { label: "training", value: "training" },
    { label: "meeting", value: "meeting" },
    { label: "research", value: "research" },
    { label: "documentation", value: "documentation" },
  ],
  EMAIL_TEMPLATE: [
    { label: "welcome", value: "welcome" },
    { label: "follow-up", value: "follow-up" },
    { label: "invoice", value: "invoice" },
    { label: "support", value: "support" },
  ],
  SCHOOLEY_MITCHELL: [
    { label: "meca_sm", value: "meca_sm" },
    { label: "post-audit", value: "post-audit" },
    { label: "online-access", value: "online-access" },
    { label: "client-coordination", value: "client-coordination" },
    { label: "cost-optimization", value: "cost-optimization" },
    { label: "audit-followup", value: "audit-followup" },
    { label: "account-setup", value: "account-setup" },
  ],
  WEST_TENNESSEE_CONSULTING: [
    { label: "wtc", value: "wtc" },
    { label: "consulting", value: "consulting" },
    { label: "project-management", value: "project-management" },
    { label: "client-meeting", value: "client-meeting" },
    { label: "proposal", value: "proposal" },
    { label: "implementation", value: "implementation" },
  ],
} as const;

export default CommonTagChips;
